var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage, FormattedDate, FormattedTime, useIntl, } from 'react-intl';
import { Button, Loading, MessageBanner, Select, } from '@folio/stripes/components';
import { useCorpusCheckFindings } from '../../api/useQuality';
import { corpusCheckFix, corpusCheckLabel, corpusCheckMeaning } from './corpusCheckLabels';
import { findingSentence } from './findingSentence';
import { diffTexts } from './textDiff';
import { recordDisplayId } from '../../utils/recordDisplayId';
import { RecordType } from '../../types/api';
var BASE_PATH = '/czna-authorities';
var PAGE_SIZE = 50;
var RECORD_TYPES = [
    RecordType.PERSONAL, RecordType.CORPORATE, RecordType.MEETING, RecordType.UNIFORM_TITLE,
    RecordType.SUBJECT, RecordType.GEOGRAPHIC, RecordType.GENRE_FORM, RecordType.CHRONOLOGICAL,
    RecordType.OLD_PRINT_CITY, RecordType.CONSPECT_GROUP, RecordType.AUTHOR_WORK,
];
/** The subfield a finding is about, marked in its MARC field. */
var markedCode = function (checkCode) {
    if (['K01', 'K02', 'K06', 'K07'].includes(checkCode))
        return 'w';
    if (['K04', 'Q4', 'Q5'].includes(checkCode))
        return 'i';
    if (['K08', 'MONTHLY_DANGLING_SEE_ALSO'].includes(checkCode))
        return 'a';
    if (checkCode === 'K09')
        return '7';
    return null;
};
/** Marked text that is not told by colour alone: bold and underlined as well. */
var markStyle = {
    background: '#fff1b8',
    color: 'inherit',
    fontWeight: 700,
    textDecoration: 'underline',
    textDecorationThickness: '2px',
    padding: 0,
};
var DiffText = function (_a) {
    var segments = _a.segments;
    return (React.createElement(React.Fragment, null, segments.map(function (s, i) { return (s.changed
        // eslint-disable-next-line react/no-array-index-key
        ? React.createElement("mark", { key: i, style: markStyle, "data-test-finding-diff": true }, s.text)
        // eslint-disable-next-line react/no-array-index-key
        : React.createElement(React.Fragment, { key: i }, s.text)); })));
};
/**
 * One side of a finding: its heading, the id it is shown by — the control number NK looks it
 * up by in Aleph, chosen by `recordDisplayId` (CZNA-977) — and a link to it.
 */
var RecordSide = function (_a) {
    var id = _a.id, authorityId = _a.authorityId, heading = _a.heading, displayId = _a.displayId, testAttr = _a.testAttr;
    var intl = useIntl();
    if (!id)
        return null;
    if (!authorityId) {
        return (React.createElement("div", __assign({}, testAttr, { style: { color: '#8a96a3', fontSize: '13px' } }),
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.findings.deleted" })));
    }
    var name = heading || displayId || authorityId;
    return (React.createElement("div", __assign({}, testAttr, { style: {
            display: 'flex', gap: '12px', alignItems: 'baseline', flexWrap: 'wrap', fontSize: '13px',
        } }),
        React.createElement("span", { style: { fontWeight: 600 } }, name),
        React.createElement("span", { style: { fontFamily: 'monospace', fontSize: '12px', color: '#5d6b7a' }, "data-test-finding-export-id": true }, displayId),
        React.createElement(Link, { to: "".concat(BASE_PATH, "/authorities/").concat(id), title: authorityId, "aria-label": "".concat(intl.formatMessage({ id: 'ui-czna-authorities.corpusQuality.findings.open' }), " ").concat(name), "data-test-finding-record-link": authorityId },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.findings.open" }))));
};
var MarcField = function (_a) {
    var field = _a.field, marked = _a.marked;
    return (React.createElement("div", { style: { fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-word' } },
        React.createElement("span", { style: { fontWeight: 700, marginRight: '10px' } }, field.tag),
        field.subfields.filter(function (sf) { return sf.value != null; }).map(function (sf, i) {
            var _a;
            var text = "$".concat((_a = sf.code) !== null && _a !== void 0 ? _a : '', " ").concat(sf.value);
            return (
            // eslint-disable-next-line react/no-array-index-key
            React.createElement("span", { key: i, style: { marginRight: '10px' } }, sf.code === marked ? React.createElement("mark", { style: markStyle }, text) : text));
        })));
};
/**
 * One finding (CZNA-1408): both records with their control numbers, a sentence saying what
 * is wrong, and the MARC field(s) as evidence — collapsed, in record order, subfields
 * spaced. A run stored before CZNA-1408 has no structured detail: its field is shown as
 * the stored string.
 */
var FindingRow = function (_a) {
    var _b, _c;
    var finding = _a.finding, checkCode = _a.checkCode;
    var intl = useIntl();
    var _d = useState(false), open = _d[0], setOpen = _d[1];
    var marcId = useId();
    var deleted = intl.formatMessage({ id: 'ui-czna-authorities.corpusQuality.findings.deleted' });
    var recordShownAs = recordDisplayId({
        type: finding.recordType, exportId: finding.exportId, authorityId: finding.authorityId,
    });
    var counterpartShownAs = recordDisplayId({
        type: finding.counterpartRecordType,
        exportId: finding.counterpartExportId,
        authorityId: finding.counterpartAuthorityId,
    });
    var nameOf = function (authorityId, heading, shownAs) { return (authorityId ? (heading || shownAs || authorityId) : deleted); };
    var sentence = findingSentence(checkCode, finding, {
        record: nameOf(finding.authorityId, finding.heading, recordShownAs),
        target: nameOf(finding.counterpartAuthorityId, finding.counterpartHeading, counterpartShownAs),
    });
    var values = __assign({}, sentence.values);
    if (checkCode === 'K08' && sentence.values.linkText != null) {
        var _e = diffTexts(sentence.values.linkText, sentence.values.targetText), link = _e[0], target = _e[1];
        values.linkText = React.createElement(DiffText, { segments: link });
        values.targetText = React.createElement(DiffText, { segments: target });
    }
    var detail = (_b = finding.detail) !== null && _b !== void 0 ? _b : null;
    var fields = [detail === null || detail === void 0 ? void 0 : detail.field, detail === null || detail === void 0 ? void 0 : detail.counterpartField]
        .filter(function (f) { return !!f; });
    var marked = markedCode(checkCode);
    // A duplicate heading is about the heading, which the sentence already names.
    var hasMarc = checkCode !== 'MONTHLY_DUP_HEADING';
    return (React.createElement("li", { "data-test-finding-row": (_c = finding.authorityId) !== null && _c !== void 0 ? _c : finding.recordId, style: { padding: '12px 14px', borderBottom: '1px solid #eef2f6' } },
        React.createElement("p", { style: { margin: '0 0 6px', fontSize: '13px', lineHeight: 1.5 }, "data-test-finding-sentence": true },
            React.createElement(FormattedMessage, { id: sentence.id, values: values })),
        React.createElement(RecordSide, { id: finding.recordId, authorityId: finding.authorityId, heading: finding.heading, displayId: recordShownAs, testAttr: { 'data-test-finding-record': true } }),
        React.createElement(RecordSide, { id: finding.counterpartId, authorityId: finding.counterpartAuthorityId, heading: finding.counterpartHeading, displayId: counterpartShownAs, testAttr: { 'data-test-finding-counterpart': true } }),
        hasMarc ? (React.createElement(React.Fragment, null,
            React.createElement("button", { type: "button", "aria-expanded": open, "aria-controls": marcId, onClick: function () { return setOpen(!open); }, "data-test-finding-marc-toggle": true, style: {
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#2c5cc5', font: 'inherit', fontSize: '12px',
                } },
                open ? '▾' : '▸',
                " ",
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.findings.marc" })),
            open ? (React.createElement("div", { id: marcId, "data-test-finding-marc": true, style: { marginTop: '6px', paddingLeft: '14px' } }, fields.length > 0
                ? fields.map(function (f, i) { return React.createElement(MarcField, { key: i, field: f, marked: marked }); }) // eslint-disable-line react/no-array-index-key
                : (React.createElement("div", { style: { fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-word' } }, finding.matchedContent)))) : null)) : null));
};
/**
 * The violating records of one corpus check in the last stored run (CZNA-1217): the record
 * and, for a check about a pair, the counterpart — for K01–K05 the record carrying the
 * one-sided link and its target. Which side to fix the list does not say; both are links.
 * For an NK question (CZNA-1220) the same list shows its cases, worded as cases.
 */
export var CorpusCheckFindings = function (_a) {
    var _b, _c, _d, _e, _f;
    var run = _a.run, isRunLoading = _a.isRunLoading, isRunError = _a.isRunError, checkCode = _a.checkCode, onBack = _a.onBack;
    var intl = useIntl();
    var _g = useState(0), offset = _g[0], setOffset = _g[1];
    var _h = useState(''), recordType = _h[0], setRecordType = _h[1];
    var findings = useCorpusCheckFindings(run === null || run === void 0 ? void 0 : run.jobId, checkCode, offset, PAGE_SIZE, recordType || null);
    var typeSelectId = 'corpus-findings-record-type';
    var stats = run === null || run === void 0 ? void 0 : run.checks.find(function (c) { return c.code === checkCode; });
    var title = corpusCheckLabel(intl, checkCode);
    // CZNA-1220: an NK question lists CASES, not findings — same list, other words.
    var isQuestion = (stats === null || stats === void 0 ? void 0 : stats.status) === 'UNDER_REVIEW';
    // The same texts the overview opens from the check name (CZNA-1407), so the list says what
    // a finding means and how to fix it without going back.
    var meaning = corpusCheckMeaning(intl, checkCode);
    var fix = isQuestion ? '' : corpusCheckFix(intl, checkCode);
    var total = (_c = (_b = findings.data) === null || _b === void 0 ? void 0 : _b.totalRecords) !== null && _c !== void 0 ? _c : 0;
    var items = (_e = (_d = findings.data) === null || _d === void 0 ? void 0 : _d.items) !== null && _e !== void 0 ? _e : [];
    return (React.createElement("div", { "data-test-corpus-check-findings": checkCode },
        React.createElement("p", { style: { margin: '0 0 12px' } },
            React.createElement("button", { type: "button", onClick: onBack, "data-test-findings-back": true, style: {
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#2c5cc5', font: 'inherit',
                } },
                "\u2190 ",
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.findings.back" }))),
        React.createElement("h2", { style: { margin: '0 0 4px', fontSize: '16px', fontWeight: 700 } }, title),
        meaning ? (React.createElement("p", { style: { margin: '0 0 4px', fontSize: '13px', color: '#5d6b7a' }, "data-test-findings-meaning": true }, meaning)) : null,
        fix ? (React.createElement("p", { style: { margin: '0 0 12px', fontSize: '13px', color: '#5d6b7a' }, "data-test-findings-fix": true },
            React.createElement("strong", null,
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.checks.fixLabel" })),
            ' ',
            fix)) : null,
        isQuestion ? (React.createElement("p", { style: { margin: '0 0 12px', fontSize: '13px', color: '#7a5600' }, "data-test-findings-question-note": true },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.findings.cases.note" }))) : null,
        isRunLoading || findings.isLoading ? React.createElement(Loading, null) : null,
        !isRunLoading && !isRunError && !run ? (React.createElement(MessageBanner, { type: "default" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.findings.noRun" }))) : null,
        isRunError || findings.isError ? (React.createElement(MessageBanner, { type: "error" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.findings.".concat(isQuestion ? 'cases.loadError' : 'loadError') }))) : null,
        run && findings.data ? (React.createElement(React.Fragment, null,
            React.createElement("p", { style: { margin: '0 0 12px', color: '#5d6b7a', fontSize: '13px' }, "data-test-findings-count": true },
                React.createElement(FormattedMessage, { id: isQuestion
                        ? 'ui-czna-authorities.corpusQuality.findings.cases.count'
                        : 'ui-czna-authorities.corpusQuality.findings.count', values: {
                        count: (_f = stats === null || stats === void 0 ? void 0 : stats.findings) !== null && _f !== void 0 ? _f : total,
                        date: (React.createElement(React.Fragment, null,
                            React.createElement(FormattedDate, { value: run.finishedAt }),
                            ' ',
                            React.createElement(FormattedTime, { value: run.finishedAt }))),
                    } })),
            React.createElement("div", { style: { maxWidth: '320px', marginBottom: '12px' } },
                React.createElement(Select, { id: typeSelectId, label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.findings.recordType" }), value: recordType, marginBottom0: true, dataOptions: __spreadArray([
                        { value: '', label: intl.formatMessage({ id: 'ui-czna-authorities.recordType.ALL' }) }
                    ], RECORD_TYPES.map(function (t) { return ({
                        value: t, label: intl.formatMessage({ id: "ui-czna-authorities.recordType.".concat(t) }),
                    }); }), true), onChange: function (e) {
                        setRecordType(e.target.value);
                        setOffset(0);
                    }, "data-test-findings-record-type": true })),
            items.length === 0 ? (React.createElement(MessageBanner, { type: "default" },
                React.createElement(FormattedMessage, { id: isQuestion
                        ? 'ui-czna-authorities.corpusQuality.findings.cases.empty'
                        : 'ui-czna-authorities.corpusQuality.findings.empty' }))) : (React.createElement("ul", { style: {
                    listStyle: 'none', margin: 0, padding: 0, border: '1px solid #d4dbe3', borderRadius: '10px', background: '#fff',
                } }, items.map(function (f, i) { return (React.createElement(FindingRow, { key: "".concat(f.recordId, "-").concat(offset + i), finding: f, checkCode: checkCode })); }))),
            total > PAGE_SIZE ? (React.createElement("div", { style: {
                    display: 'flex', gap: '12px', alignItems: 'center', marginTop: '12px',
                } },
                React.createElement(Button, { onClick: function () { return setOffset(Math.max(offset - PAGE_SIZE, 0)); }, disabled: offset === 0 || findings.isFetching, marginBottom0: true, "data-test-findings-prev": true },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.findings.prev" })),
                React.createElement("span", { style: { fontSize: '13px', color: '#5d6b7a' } },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.findings.page", values: { from: offset + 1, to: Math.min(offset + PAGE_SIZE, total), total: total } })),
                React.createElement(Button, { onClick: function () { return setOffset(offset + PAGE_SIZE); }, disabled: offset + PAGE_SIZE >= total || findings.isFetching, marginBottom0: true, "data-test-findings-next": true },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.corpusQuality.findings.next" })))) : null)) : null));
};
